import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import {
  distributeTextCanvas,
  getOptimalFontSize,
} from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var39');

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('image-var39');

            const leftText = document.createElement('div');
            leftText.classList.add('left-text-var39');

            const leftTextP = document.createElement('p');
            let replacedLeftText = replaceQuotes(variation.leftText);

            const leftAlgorithmResult = getOptimalFontSize(replacedLeftText, {
              maxWidth: 520,
              maxHeight: 560,
              fontFamily: 'Gothic',
              minFontSize: 45,
              maxFontSize: 120,
              lineHeight: '1.2',
            });

            if (variation.leftBrakes) {
              leftTextP.innerText = replacedLeftText;
            } else {
              leftTextP.innerHTML = leftAlgorithmResult.lines.join('<br/>');
            }

            if (variation.leftFontSize) {
              leftText.style.fontSize = `${variation.leftFontSize}px`;
            } else {
              leftText.style.fontSize = `${leftAlgorithmResult.fontSize}px`;
            }

            leftText.appendChild(leftTextP);

            imageHolder.appendChild(leftText);

            const rightDiv = document.createElement('div');
            rightDiv.classList.add('right-div-var39');
            if (variation.reversed) {
              rightDiv.classList.add('right-div-var39--reversed');
            }
            rightDiv.appendChild(rightImage);

            const rightActualPhoto = createWatermarkTagImage(
              'right-actual-photo-var39',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightActualPhoto) {
              if (variation.reversed) {
                rightActualPhoto.classList.add(
                  'right-actual-photo-var39--reversed',
                );
              }
              rightDiv.appendChild(rightActualPhoto);
            }

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              middleBreak.classList.add('break-var39');

              if (variation.divider === 'black') {
                middleBreak.classList.add('break-var39--black');
              }

              imageHolder.appendChild(middleBreak);
            }

            if (variation.rightText) {
              const rightText = document.createElement('div');
              rightText.classList.add('right-text-var39');

              if (variation.divider !== 'none') {
                rightText.classList.add('right-text-var39__divider');
                if (variation.reversed) {
                  rightText.classList.add('right-text-var39__divider-reversed');
                }
              }

              const rightP = document.createElement('p');
              const updatedText = replaceQuotes(variation.rightText);
              let text = '';
              if (variation.rightQuotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }

              if (variation.rightBreakes) {
                rightP.innerText = text;
                rightP.classList.add('custom-brakes-var39');
              } else {
                rightP.innerHTML = distributeTextCanvas(text, {
                  maxWidth: 466,
                  fontFamily: 'CustomArial',
                  minFontSize: 45,
                });
              }

              rightText.appendChild(rightP);

              if (variation.rightTextPositon === 'top') {
                rightText.classList.add('right-text-var39--top');
              }

              rightDiv.appendChild(rightText);
            }

            imageHolder.appendChild(rightDiv);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var39',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
